// src/services/coreService.js
import apiClient from './api';

// --- Standard Core API Functions ---

export const getHomeData = async () => {
  try {
    const response = await apiClient.get('/core/home/');
    return response.data;
  } catch (error) {
    console.error("coreService: Failed to fetch home data:", error.response?.data || error.message);
    throw error;
  }
};

export const getStartData = async () => {
  try {
    const response = await apiClient.get('/core/start/');
    return response.data;
  } catch (error) {
    console.error("coreService: Failed to fetch start data:", error.response?.data || error.message);
    throw error;
  }
};

export const createTask = async () => {
  try {
    const response = await apiClient.post('/core/create-task/');
    return response.data;
  } catch (error) {
    console.error("coreService: Failed to create task:", error.response?.data || error.message);
    throw error;
  }
};

export const getRecords = async () => {
   try {
       const response = await apiClient.get('/core/records/');
       return response.data;
   } catch (error) {
       console.error("coreService: Failed to fetch records:", error.response?.data || error.message);
       throw error;
   }
};

export const submitTask = async (taskId) => {
   if (!taskId) throw new Error("Task ID is required to submit.");
   try {
       const response = await apiClient.post(`/core/task/${taskId}/`);
       return response.data;
   } catch (error) {
       console.error(`coreService: Failed to submit task ${taskId}:`, error.response?.data || error.message);
       throw error;
   }
};

// --- Financial / PIN Related Functions ---

/**
 * Verifies the user's withdrawal PIN using POST /core/verify-withdraw-pin/.
 * Sends PIN in the request body as 'transaction_password'.
 */
export const verifyWithdrawPin = async (pinValue) => {
    try {
        const payload = { transaction_password: pinValue };
        const response = await apiClient.post('/core/verify-withdraw-pin/', payload);
        return response.data;
    } catch (error) {
        console.error("coreService: Failed to verify PIN (POST):", error.response?.data || error.message);
        if (error.response && (error.response.status === 400 || error.response.status === 401 || error.response.status === 403)) {
             throw new Error(error.response.data?.error || error.response.data?.detail || "Invalid PIN provided.");
        }
        throw new Error("PIN verification failed. Please try again later.");
    }
};

/**
 * Updates wallet info using POST /core/update-transaction-password/. Requires verified PIN.
 */
export const updateWalletInfo = async (transactionPassword, walletAddress, walletType) => {
    const payload = {
        transaction_password: transactionPassword,
        wallet_address: walletAddress,
        wallet_type: walletType,
    };
    try {
        const response = await apiClient.post('/core/update-transaction-password/', payload);
        return response.data;
    } catch (error) {
        console.error("coreService: Failed to update wallet info (POST):", error.response?.data || error.message);
        throw new Error(error.response?.data?.error || "Failed to update wallet information.");
    }
};

export const requestWithdrawal = async (amount, withdrawalPassword) => {
  const payload = {
      amount: parseFloat(amount),
      withdrawal_password: withdrawalPassword, 
  };
  try {
      const response = await apiClient.post('/core/withdraw/', payload);
      return response.data;
  } catch (error) {
      console.error("coreService: Failed to request withdrawal:", error.response?.data || error.message);
      throw new Error(error.response?.data?.error || "Failed to submit withdrawal request.");
  }
};

export const getTransactionHistory = async () => {
  try {
      const response = await apiClient.get('/core/withdraw/'); 
      return response.data?.transactions || response.data || [];
  } catch (error) {
      console.error("coreService: Failed to fetch transaction history:", error.response?.data || error.message);
      throw new Error(error.response?.data?.error || "Failed to fetch transaction history.");
  }
};

// --- Profile Update Functions ---

export const updateProfileImage = async (imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  try {
      const response = await apiClient.post('/core/edit-profile/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
  } catch (error) {
      console.error("coreService: Failed to update profile image:", error.response?.data || error.message);
      throw new Error(error.response?.data?.error || "Failed to update profile image.");
  }
};

export const modifyPassword = async (formType, oldPassword, newPassword, confirmNewPassword) => {
  const payload = {
      form_type: formType, 
      old_password: oldPassword,
      new_password: newPassword,
      confirm_new_password: confirmNewPassword,
  };
  try {
      const response = await apiClient.post('/core/modify-password/', payload);
      return response.data;
  } catch (error) {
      console.error(`coreService: Failed to modify ${formType} password:`, error.response?.data || error.message);
      throw new Error(error.response?.data?.error || `Failed to update ${formType === 'login' ? 'password' : 'PIN'}.`);
  }
};




/**
 * Fetches the user's current check-in status from the backend.
 * GET /api/check-in/
 * Expected backend response: { date: "YYYY-MM-DD" | null, reward: number, current_streak: number }
 * @returns {Promise<object|null>} - Promise resolving with check-in data or null.
 * @throws {Error} - Throws an error if the API call fails unexpectedly.
 */
export const getCheckInStatus = async () => {
  try {
    // Adjust path if your apiClient base URL doesn't include '/api'
    const response = await apiClient.get('account/check-in/');
    return response.data && Object.keys(response.data).length > 0 ? response.data : null;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      console.log("coreService: No check-in record found (404).");
      return null; // Treat as never checked in
    }
    console.error("coreService: Failed to GET check-in status:", error.response?.data || error.message);
    throw new Error(error.response?.data?.error || "Failed to load check-in status.");
  }
};

/**
 * Performs the check-in action via the backend.
 * POST /api/check-in/
 * @returns {Promise<object>} - The promise resolving with the API response data.
 * @throws {Error} - Throws an error if the API call fails (e.g., already checked in).
 */
export const performCheckIn = async () => {
  try {
    // Adjust path if needed
    const response = await apiClient.post('/account/check-in/');
    return response.data;
  } catch (error) {
    console.error("coreService: Check-in POST failed:", error.response?.data || error.message);
    throw new Error(error.response?.data?.error || "Check-in failed. You may have already checked in today or an error occurred.");
  }
};